import React, { useEffect, useRef, useState } from "react";
import { ukDayYear as fmtDate } from "@/lib/time";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileSignature, FileDown, Loader2, ShieldCheck, CheckCircle2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { SignatureField } from "./SignatureField";


/**
 * AgreementChip — self-contained portal chip showing agreement status.
 * Opens the premium review-and-sign dialog. Renders nothing for non-portal roles.
 */
export const AgreementChip = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { const r = await api.get("/portal/agreement"); setData(r.data); } catch { /* not a portal user */ }
  };
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const signed = data.status === "signed";
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="portal-agreement-chip"
              className={`inline-flex items-center gap-2 rounded-full border px-3.5 h-8 text-[12px] font-medium transition-colors ${
                signed
                  ? "border-teal-400/30 bg-teal-500/10 text-teal-300 hover:bg-teal-500/15"
                  : "border-white/15 bg-white/[0.05] text-white/85 hover:bg-white/[0.1]"
              }`}>
        {signed ? <ShieldCheck className="size-3.5" /> : (
          <span className="relative flex size-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7da9ff] opacity-60" />
            <span className="relative inline-flex rounded-full size-2 bg-[#7da9ff]" />
          </span>
        )}
        {signed ? `Agreement signed ${fmtDate(data.signed_at)}` : "View & sign your agreement"}
      </button>
      <AgreementDialog open={open} onOpenChange={setOpen} data={data} onSigned={load} />
    </>
  );
};

/* AgreementCallout — prominent one-time "sign your agreement" card (unsigned only). */
export const AgreementCallout = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try { const r = await api.get("/portal/agreement"); setData(r.data); } catch { /* not a portal user */ }
  };
  useEffect(() => { load(); }, []);
  if (!data || data.status === "signed") return null;
  const reissued = data.reissued_for === "terms_change";
  return (
    <>
      <div className="rounded-2xl border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] p-4 sm:p-5" data-testid="agreement-callout">
        <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
          <span className="grid place-items-center size-10 rounded-xl bg-[#7da9ff]/[0.18] border border-[#7da9ff]/45 shrink-0">
            <PenLine className="size-4.5 text-[#3B63A8]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[14.5px] font-semibold text-slate-900">
              {reissued
                ? `Your terms changed — here's the updated agreement to countersign`
                : "One quick step before your first payout: sign your agreement"}
            </div>
            {reissued && data.terms?.short && (
              <div className="mt-1 text-[12.5px] text-[#24457F]" data-testid="agreement-callout-terms">
                Now <b>{data.terms.short}</b>. Your previous agreement has been superseded and archived — it stays
                in your Terms history.
              </div>
            )}
            <div className="mt-1.5 grid sm:grid-cols-3 gap-2 text-[12px] text-[#2F5390]">
              <span className="inline-flex items-center gap-1.5"><span className="grid place-items-center size-4.5 rounded-full bg-[#7da9ff]/[0.28] text-[10px] font-bold">1</span> Read the short terms</span>
              <span className="inline-flex items-center gap-1.5"><span className="grid place-items-center size-4.5 rounded-full bg-[#7da9ff]/[0.28] text-[10px] font-bold">2</span> Sign with your finger or mouse</span>
              <span className="inline-flex items-center gap-1.5"><span className="grid place-items-center size-4.5 rounded-full bg-[#7da9ff]/[0.28] text-[10px] font-bold">3</span> Done — takes under 2 minutes</span>
            </div>
          </div>
          <Button onClick={() => setOpen(true)} className="rounded-full bg-[#0B1220] hover:bg-[#1a2436] text-white shrink-0" data-testid="agreement-callout-sign">
            Review &amp; sign now
          </Button>
        </div>
      </div>
      <AgreementDialog open={open} onOpenChange={setOpen} data={data} onSigned={load} />
    </>
  );
};

export const AgreementDialog = ({ open, onOpenChange, data, onSigned, doc = "agreement" }) => {
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [signing, setSigning] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [justSigned, setJustSigned] = useState(false);
  const padRef = useRef(null);

  useEffect(() => { if (open) { setName(""); setAgree(false); setJustSigned(false); } }, [open]);
  if (!data) return null;

  const isNda = doc === "nda";
  const signed = data.status === "signed" || justSigned;
  const label = data.title || (isNda ? "Mutual NDA" : data.kind === "lender" ? "Lender panel agreement" : "Introducer agreement");

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/portal/${doc}.pdf`, { params: { v: Date.now() }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `connexis-${isNda ? "mutual-nda" : "agreement"}${signed ? "-signed" : ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  const downloadScan = async () => {
    try {
      const r = await api.get(`/portal/${doc}/scan`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `connexis-${doc}-signed-scan`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Scan download failed"); }
  };

  const sign = async () => {
    if (!name.trim() || !agree) return;
    if (padRef.current?.isEmpty()) { toast.error("Add your signature — type your name or draw it"); return; }
    setSigning(true);
    try {
      await api.post(`/portal/${doc}/sign`, { signed_name: name.trim(), signature_png: padRef.current.toDataURL() });
      setJustSigned(true);
      toast.success(`${isNda ? "NDA" : "Agreement"} countersigned — a fully executed copy is on its way to your inbox`);
      onSigned?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Signing failed"); }
    finally { setSigning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid={isNda ? "portal-nda-dialog" : "portal-agreement-dialog"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSignature className="size-4 text-[#0055FF]" /> {label}</DialogTitle>
          <DialogDescription>Between Connexis Group Ltd and {data.party_name}</DialogDescription>
        </DialogHeader>

        {/* Agreed terms / NDA scope */}
        <div className="relative overflow-hidden rounded-2xl bg-[#0B1220] text-white p-5">
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
          <div className="absolute -top-12 -right-8 w-40 h-40 rounded-full bg-[#0055FF]/25 blur-3xl pointer-events-none" />
          <div className="relative">
            {isNda ? (
              <>
                <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#7da9ff]">Mutual confidentiality</div>
                <div className="mt-1.5 text-[20px] font-semibold tracking-[-0.02em]">Both ways, 3 years</div>
                <div className="mt-1 text-[13px] text-white/75">Applicant financials, lending criteria, panel composition, commissions and deal flow.</div>
                <div className="mt-1 text-[11.5px] text-white/45">UK GDPR aligned · governed by the laws of England &amp; Wales.</div>
              </>
            ) : (
              <>
                <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#7da9ff]">Agreed commercial terms</div>
                <div className="mt-1.5 text-[26px] font-semibold tracking-[-0.02em] tabular-nums" data-testid="agreement-rate">{data.terms?.rate}</div>
                <div className="mt-0.5 text-[13px] text-white/75">{data.terms?.short}</div>
                <div className="mt-1 text-[11.5px] text-white/45">{data.terms?.schedule}.</div>
              </>
            )}
          </div>
        </div>

        {!signed && (
          <div className="rounded-xl border border-[#0055FF]/20 bg-[#0055FF]/[0.05] px-3.5 py-2.5 text-[12.5px] text-slate-700">
            <b className="text-slate-900">Connexis has already signed this.</b> Add your countersignature below and it becomes fully executed —
            we'll email you the completed copy straight away.
          </div>
        )}

        <Button variant="outline" onClick={download} disabled={downloading} className="w-full rounded-xl" data-testid={isNda ? "nda-download-btn" : "agreement-download-btn"}>
          {downloading ? <Loader2 className="size-4 animate-spin" /> : <><FileDown className="size-4 mr-1.5" /> Download the full {isNda ? "NDA" : "agreement"} (PDF)</>}
        </Button>

        {signed ? (
          <div className="space-y-3">
            <div className="rounded-2xl border border-teal-200 bg-teal-50 p-4 flex items-start gap-3" data-testid={isNda ? "nda-signed-banner" : "agreement-signed-banner"}>
              <CheckCircle2 className="size-5 text-teal-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-[13.5px] font-semibold text-teal-800">Fully executed</div>
                <div className="mt-0.5 text-[12.5px] text-teal-700">
                  Countersigned{data.signed_name || justSigned ? ` by ${data.signed_name || name}` : ""} {data.signed_at ? `on ${fmtDate(data.signed_at)}` : "just now"}
                  {data.method === "wet_ink" ? " by wet-ink signature (scan on file)" : ""}. A signed copy has been emailed to you — re-download it above any time.
                </div>
              </div>
            </div>
            {data.has_scan && (
              <Button variant="outline" onClick={downloadScan} className="w-full rounded-xl" data-testid="agreement-scan-download">
                <FileDown className="size-4 mr-1.5" /> Download your countersigned scan
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-600 mb-2">Your signature</div>
                <SignatureField ref={padRef} name={name} onNameChange={setName} testId="agreement-signature" />
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer" data-testid="agreement-sign-confirm">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 size-4 accent-[#0055FF]" />
                <span className="text-[12px] text-slate-600 leading-relaxed">
                  I confirm I have read the {isNda ? "NDA" : "agreement"}, I am authorised to sign on behalf of my firm, and I agree my electronic signature is legally binding.
                </span>
              </label>
            </div>
            <Button onClick={sign} disabled={signing || name.trim().length < 2 || !agree}
                    className="w-full h-11 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] text-[14px] font-semibold"
                    data-testid={isNda ? "nda-sign-submit" : "agreement-sign-submit"}>
              {signing ? <Loader2 className="size-4 animate-spin" /> : <><FileSignature className="size-4 mr-1.5" /> Countersign {isNda ? "NDA" : "agreement"}</>}
            </Button>
            <div className="text-center text-[10.5px] text-slate-400">
              Your name, signature, date and IP address will be recorded and embedded in the executed PDF.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
